/**
 * @file Translates abstract input into player intent (GDD §5, §6).
 *
 * Movement and aim are fully independent: the move vector is screen-relative
 * while the facing angle comes from the cursor, so the player can strafe
 * backwards while shooting sideways. The system writes intent only — the actual
 * integration is the movement system's job.
 */

import * as THREE from 'three';
import { System } from '../ecs/System.js';
import {
  Transform,
  Velocity,
  PlayerControlled,
  Weapon,
  statMultiplier
} from '../components/index.js';
import { Stat } from '../config/Powerups.js';
import { getPlayerTuning } from '../config/PlayerTuning.js';
import { Action } from '../input/InputManager.js';
import { Events } from '../core/EventBus.js';

/**
 * Constant factor applied to the base move speed. The game has no walk/sprint
 * toggle: the player always moves at what used to be the sprint pace (issue #61).
 */
export const MOVE_SPEED_MULTIPLIER = 1.65;

/**
 * Footsteps come faster than the raw distance/speed relation would give, so the
 * cadence reads as a run rather than a stroll.
 */
export const FOOTSTEP_INTERVAL_SCALE = 0.72;

export class PlayerControlSystem extends System {
  static priority = 10;

  constructor() {
    super();
    /** @type {{ x: number, y: number }} @private */
    this._move = { x: 0, y: 0 };
    /** @type {THREE.Raycaster} @private */
    this._raycaster = new THREE.Raycaster();
    /** @type {THREE.Plane} Aim plane, sits at muzzle height. @private */
    this._aimPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    /** @type {THREE.Vector3} @private */
    this._hit = new THREE.Vector3();
  }

  /** @returns {void} */
  init() {
    this._aimPlane.constant = -getPlayerTuning().aimHeight;
  }

  /**
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   */
  update(dt) {
    const { input } = this.ctx;
    const entities = this.world.query(PlayerControlled, Transform, Velocity);

    // Read the live build once per frame: an edit in the sandbox reaches the
    // player on the next frame, no reload. With no overrides this is
    // `CONFIG.player` value for value.
    const tuning = getPlayerTuning();
    // Aim height can be tuned live, so the projection plane is refreshed here
    // rather than pinned once in init().
    this._aimPlane.constant = -tuning.aimHeight;

    for (const entity of entities) {
      const control = entity.get(PlayerControlled);
      const transform = entity.get(Transform);
      const velocity = entity.get(Velocity);

      this._applyMovement(entity, control, velocity, tuning, dt);
      this._applyAim(control, transform, tuning);

      // Intent only: the weapon decides whether either request is honoured.
      const weapon = entity.get(Weapon);
      if (weapon) {
        weapon.triggerHeld = input.isDown(Action.FIRE);
        if (input.justPressed(Action.RELOAD)) weapon.reload();
      }
    }
  }

  /**
   * Screen-relative movement. `-Z` is "up" on screen because the camera always
   * looks down the negative Z axis.
   *
   * @param {import('../ecs/Entity.js').Entity} entity Player entity.
   * @param {import('../components/PlayerControlled.js').PlayerControlled} control Player state.
   * @param {import('../components/Velocity.js').Velocity} velocity Velocity component.
   * @param {typeof import('../config/GameConfig.js').CONFIG.player} tuning Live player build.
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   * @private
   */
  _applyMovement(entity, control, velocity, tuning, dt) {
    const { input, events } = this.ctx;
    const move = input.getMoveVector(this._move);

    // Accel/brake rates are baked onto the component at spawn; pushing the live
    // values here each frame is what lets the sandbox tune them without a respawn.
    velocity.acceleration = tuning.acceleration;
    velocity.deceleration = tuning.deceleration;

    const speed =
      tuning.speed *
      MOVE_SPEED_MULTIPLIER *
      statMultiplier(entity, Stat.SPEED);

    velocity.maxSpeed = speed;
    velocity.desired.set(move.x * speed, 0, move.y * speed);

    // Footsteps are distance-based, so they stay in sync at any speed.
    const travelled = velocity.linear.length() * dt;
    if (travelled > 0.001) {
      control.stepDistance += travelled;
      const interval = tuning.footstepInterval * FOOTSTEP_INTERVAL_SCALE * speed;
      if (control.stepDistance >= interval) {
        control.stepDistance = 0;
        events.emit(Events.FOOTSTEP, { entity });
      }
    }
  }

  /**
   * Projects the cursor onto the aim plane and turns the body toward it.
   *
   * The body snaps to the aim angle instantly rather than easing toward it:
   * `WeaponSystem` independently recomputes the same angle from `aimPoint` to
   * fire, so any smoothing here would desync the two — the character (and its
   * gun) would visibly face somewhere other than where the shot actually goes
   * whenever the cursor moves faster than the smoothing could track.
   *
   * @param {import('../components/PlayerControlled.js').PlayerControlled} control Player state.
   * @param {import('../components/Transform.js').Transform} transform Player transform.
   * @param {typeof import('../config/GameConfig.js').CONFIG.player} tuning Live player build.
   * @returns {void}
   * @private
   */
  _applyAim(control, transform, tuning) {
    const { input, cameraController } = this.ctx;

    if (input.analogActive && (input.aimStick.x !== 0 || input.aimStick.y !== 0)) {
      // Right stick aims directly: no plane projection needed.
      control.aimPoint.set(
        transform.position.x + input.aimStick.x * 10,
        tuning.aimHeight,
        transform.position.z + input.aimStick.y * 10
      );
    } else {
      this._raycaster.setFromCamera(
        /** @type {any} */ ({ x: input.pointer.ndcX, y: input.pointer.ndcY }),
        cameraController.camera
      );
      if (!this._raycaster.ray.intersectPlane(this._aimPlane, this._hit)) return;
      control.aimPoint.copy(this._hit);
    }

    const dx = control.aimPoint.x - transform.position.x;
    const dz = control.aimPoint.z - transform.position.z;
    if (dx * dx + dz * dz < 1e-6) return;

    transform.rotationY = Math.atan2(dx, dz);
  }
}
